import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import {
  ImportFileContainer,
  StyledFileBox,
  StyledFileInput,
  StyledFileInputContainer,
  StyledFileType,
} from './styles';
import { EMIMETypes, EMIMETypesLabel } from 'constants/MIMETypes';
import FormLabel from 'components/atoms/FormLabel';
import { EIconTypes } from 'constants/Icons';
import Button from 'components/atoms/Button';
import { EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { FieldErrors, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { ATTACHMENT_NAME_REGEX } from 'utils/attachments';
import PDFViewer from '../PDFViewer';
import FormWarningLabel from 'components/atoms/FormWarningLabel';

const MAX_SIZE_KB = 5 * 1024;

interface FileEntry {
  file: File;
  displayName: string;
  ext: string;
}
export interface IFilePrev {
  fileUrl: string;
  type: EMIMETypesLabel;
  name?: string;
}
interface IFileDropzone {
  name: string;
  fileName?: string;
  key?: string;
  label: string;
  multiple?: boolean;
  errors?: any;
  setValue: any;
  allowedType: EMIMETypes[];
  maxSizeKB?: number;
  showValidationInMB?: boolean;
  disabled?: boolean;
  required?: boolean;
  disabledInputName?: boolean;
  showPreviewFile?: boolean;
  initValue?: IFilePrev;
  allowedTypeIsDisabled?: EMIMETypes[];
  deleteInitFile?: () => void;
  additionalValidate?: (
    file: File,
  ) => Promise<{ errorMessage: string | null; warningMessages: string[] | null }>;
}

const getNameAndExt = (filename: string) => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return { name: filename, ext: '' };
  return {
    name: filename.slice(0, lastDot),
    ext: filename.slice(lastDot),
  };
};

const allowedTypesLabel = (allowedType: EMIMETypes[]) => {
  const labels: EMIMETypesLabel[] = [];
  allowedType.forEach((item) => {
    switch (item) {
      case EMIMETypes.JPG:
        labels.push(EMIMETypesLabel.JPG);
        break;
      case EMIMETypes.PNG:
        labels.push(EMIMETypesLabel.PNG);
        break;
      case EMIMETypes.PDF:
        labels.push(EMIMETypesLabel.PDF);
        break;
      default:
        break;
    }
  });
  return labels.join(', ');
};

const getErrorByPath = (errors: FieldErrors, path: string): string | undefined => {
  const value = path.split('.').reduce<any>((acc, key) => acc?.[key], errors);
  if (!value) return undefined;

  if (typeof value.message === 'string') return value.message;

  if (Array.isArray(value)) {
    const first = value.find((item) => item?.message);
    return first?.message;
  }

  return undefined;
};

export const FileDropzoneContainer = ({
  name,
  fileName,
  label,
  multiple = false,
  setValue,
  allowedType,
  maxSizeKB = MAX_SIZE_KB,
  showValidationInMB = true,
  disabled,
  disabledInputName = true,
  showPreviewFile = false,
  required,
  errors,
  key,
  initValue,
  allowedTypeIsDisabled = [],
  deleteInitFile,
  additionalValidate,
}: IFileDropzone) => {
  const { t } = useTranslations();
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const formError = errors ? getErrorByPath(errors, name) : undefined;
  const [filePrev, setFilePrev] = useState<IFilePrev | null>(initValue ?? null);

  useEffect(() => {
    if (key) {
      setEntries([]);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [key]);

  useEffect(() => {
    const files = entries.map((e) => e.file);
    const filesNames = entries.map((e) => e.displayName);

    if (showPreviewFile && entries.length) {
      setFilePrev({
        fileUrl: URL.createObjectURL(entries[0]?.file),
        type:
          entries[0]?.file.type === EMIMETypes.PDF ? EMIMETypesLabel.PDF : EMIMETypesLabel.JPG,
      });
    }

    if (!multiple) {
      setValue(name, files[0] ?? null);
      if (!disabledInputName && fileName) {
        setValue(fileName, filesNames[0]);
      }
    } else {
      setValue(name, files);
      if (!disabledInputName && fileName) {
        setValue(fileName, filesNames);
      }
    }
  }, [entries, multiple, name, setValue]);

  useEffect(() => {
    if (disabled) {
      setEntries([]);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [disabled]);

  const validateFiles = useCallback(
    async (selectedFiles: FileList | null): Promise<File[]> => {
      if (!selectedFiles) return [];
      const valid: File[] = [];

      for (const file of Array.from(selectedFiles)) {
        if (
          !allowedTypeIsDisabled.includes(file.type as EMIMETypes) &&
          !allowedType.includes(file.type as EMIMETypes)
        ) {
          setError(
            t('file.allowedFileFormats.label').replace('{0}', allowedTypesLabel(allowedType)),
          );
          return [];
        }

        if (file.size > maxSizeKB * 1024) {
          showValidationInMB
            ? setError(
                t('manageImages.maxSizeMB.label').replace(
                  '{0}',
                  (maxSizeKB / 1024).toString(),
                ),
              )
            : setError(t('manageImages.maxSizeKB.label').replace('{0}', maxSizeKB.toString()));
          return [];
        }

        if (!file.name?.match(ATTACHMENT_NAME_REGEX)) {
          setError(t('addAttachment.fileNameValidation.text'));
          return [];
        }

        if (additionalValidate) {
          const { errorMessage, warningMessages } = await additionalValidate(file);
          if (errorMessage) {
            setError(errorMessage);
            setWarning(null);
            return [];
          } else if (warningMessages && warningMessages.length > 0) {
            setWarning(
              warningMessages.map((message, key) => `${key + 1}. ${t(message)}`).join('\n'),
            );
          }
        }

        valid.push(file);
      }

      if (valid.length > 0) setError(null);
      return valid;
    },
    [allowedType, allowedTypeIsDisabled, maxSizeKB, t, additionalValidate],
  );

  const addFiles = useCallback(
    async (selectedFiles: FileList | null) => {
      const validated = await validateFiles(selectedFiles);
      if (!validated.length) return;

      setEntries((prev) => {
        const base = multiple ? prev : [];
        const newEntries: FileEntry[] = validated.map((file) => {
          const { name: baseName, ext } = getNameAndExt(file.name);
          return { file, displayName: baseName, ext };
        });
        return [...base, ...newEntries];
      });

      if (inputRef.current) inputRef.current.value = '';
    },
    [multiple, validateFiles],
  );

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) addFiles(e.target.files);
  };

  const handleRename = (index: number, newDisplayName: string) => {
    setEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, displayName: newDisplayName } : entry,
      ),
    );
  };

  const handleRemove = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
    setFilePrev(null);
  };

  return (
    <Grid container>
      <FormLabel name={name} label={label} required={required} />

      {showPreviewFile && filePrev ? (
        <Grid container size={{ xs: 12 }} rowSpacing={1} sx={{ mb: 2 }}>
          {filePrev.type === EMIMETypesLabel.PDF ? (
            <PDFViewer pdfUrl={filePrev.fileUrl} height={320} />
          ) : (
            <img
              src={filePrev.fileUrl}
              alt="file"
              style={{
                width: 'auto',
                maxWidth: '100%',
                maxHeight: '320px',
                objectFit: 'cover',
              }}
            />
          )}
          {!entries.length && (
            <Grid size={{ xs: 12 }} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
              <Grid
                container
                size={{ xs: 12 }}
                sx={{ alignItems: 'center', flexWrap: 'nowrap', gap: '8px' }}
              >
                <StyledFileInputContainer disabledStyle={true}>
                  <StyledFileBox>
                    <StyledFileInput
                      value={filePrev.name}
                      disabled={true}
                      disabledStyle={true}
                    />
                    <StyledFileType>{filePrev.type}</StyledFileType>
                  </StyledFileBox>
                </StyledFileInputContainer>
                <Grid>
                  <Button
                    variant={EButtonVariants.outlined}
                    icon={EIconTypes.delete}
                    onClick={() => {
                      if (deleteInitFile) {
                        deleteInitFile();
                      }
                      setFilePrev(null);
                    }}
                    sizePX={EButtonSizesPX.px32}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      ) : (
        <ImportFileContainer
          container
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => inputRef.current?.click()}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          disabled={disabled}
          error={!!(error || formError)}
        >
          <Grid sx={{ gap: '4px' }}>
            <Typography variant="body2" sx={{ color: colors.primary600 }}>
              {t('file.addFile.label')}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.gray400 }}>
              {t('file.dragDropFile.label')}
            </Typography>
          </Grid>

          <Grid sx={{ gap: '4px' }}>
            <Typography variant="caption" sx={{ color: colors.gray400 }}>
              {t('file.allowedFileFormats.label').replace(
                '{0}',
                allowedTypesLabel(allowedType),
              )}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray400, textAlign: 'center' }}>
              {showValidationInMB
                ? t('manageImages.maxSizeMB.label').replace(
                    '{0}',
                    (maxSizeKB / 1024).toString(),
                  )
                : t('manageImages.maxSizeKB.label').replace('{0}', maxSizeKB.toString())}
            </Typography>
          </Grid>

          <input
            ref={inputRef}
            id={name}
            type="file"
            accept={allowedType.join(', ')}
            hidden
            onChange={handleFileInputChange}
            multiple={multiple}
            disabled={disabled}
          />
        </ImportFileContainer>
      )}
      {(error || formError) && (
        <Grid sx={{ pt: '5px' }}>
          <FormErrorLabel
            label={(error ?? formError) as string}
            style={{ whiteSpace: 'pre-line' }}
          />
        </Grid>
      )}
      {warning && (
        <Grid sx={{ pt: '5px' }}>
          <FormWarningLabel label={warning as string} style={{ whiteSpace: 'pre-line' }} />
        </Grid>
      )}

      {entries.length > 0 && (
        <Grid
          container
          size={{ xs: 12 }}
          sx={{ mt: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}
        >
          {entries.map((entry, index) => (
            <Grid
              key={index}
              container
              size={{ xs: 12 }}
              sx={{ alignItems: 'center', flexWrap: 'nowrap', gap: '8px' }}
            >
              <StyledFileInputContainer>
                <StyledFileBox>
                  <StyledFileInput
                    value={entry.displayName}
                    onChange={(e) => handleRename(index, e.target.value)}
                    inputProps={{ 'aria-label': `Nazwa pliku ${index + 1}` }}
                    disabled={disabledInputName}
                    disabledStyle={false}
                  />
                  <StyledFileType>{entry.ext}</StyledFileType>
                </StyledFileBox>
              </StyledFileInputContainer>
              <Grid>
                <Button
                  id={`${name}_button_${index}`}
                  variant={EButtonVariants.outlined}
                  icon={EIconTypes.delete}
                  onClick={() => handleRemove(index)}
                  sizePX={EButtonSizesPX.px32}
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );
};
